import type { ReactNode } from 'react'

import { PageBackButton, PageBackLink } from '@/customer/components/page-back-link'
import { PageDescription } from '@/customer/components/page-description'
import { StackedHeadline, type StackedHeadlineItem } from '@/customer/components/stacked-headline'
import { cn } from '@/customer/lib/utils'

type PageContainerProps = {
  width?: 'default' | 'wide' | 'full'
  headline?: StackedHeadlineItem[]
  description?: string
  /** Centers the headline and content. */
  center?: boolean
  backLink?:
    | { to: string; label: string; onClick?: never }
    | { onClick: () => void; label: string; to?: never }
  className?: string
  children?: ReactNode
}

const widthClass = {
  default: 'max-w-xl lg:max-w-3xl',
  wide: 'max-w-4xl',
  full: '',
} satisfies Record<NonNullable<PageContainerProps['width']>, string>

/**
 * Width-capped, centred wrapper for portal page content. `width`
 * selects the max width: `default` = `max-w-xl` (`max-w-3xl` from `lg`),
 * `wide` = `max-w-4xl`, `full` = uncapped.
 *
 * Optional `headline` and `backLink` render the standard page header
 * with consistent spacing.
 */
export function PageContainer({
  width = 'default',
  headline,
  description,
  center,
  backLink,
  className,
  children,
}: PageContainerProps) {
  const hasHeader = headline || description || backLink

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col',
        widthClass[width],
        center && 'items-center text-center',
        className
      )}
    >
      {hasHeader && (
        <div>
          {backLink && (
            <div className="mb-4">
              {'to' in backLink && backLink.to ? (
                <PageBackLink to={backLink.to}>{backLink.label}</PageBackLink>
              ) : (
                <PageBackButton onClick={backLink.onClick!}>{backLink.label}</PageBackButton>
              )}
            </div>
          )}
          {headline && (
            <StackedHeadline
              lines={headline}
              align={center ? 'center' : 'left'}
              margin={!description}
            />
          )}
          {description && (
            <PageDescription className="mt-6" margin>
              {description}
            </PageDescription>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
